import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { annotationModels, type AnnotationProvider, type AnnotationSelection } from '@/api/annotationModels'
import { Button } from '@/components/ui/button'

const selectClass = 'h-11 w-full rounded-xl border border-line-strong bg-paper px-3 text-sm text-ink focus-visible:focus-ring'

export function useAnnotationModel(enabled = true, preferredProvider?: AnnotationProvider) {
  const query = useQuery({ queryKey: ['annotation-models'], queryFn: ({ signal }) => annotationModels(signal), enabled, staleTime: 30_000, retry: false })
  const [picked, setPicked] = useState<AnnotationSelection | null>(null)
  const choices = query.data?.providers ?? []
  const available = choices.filter((p) => p.available && p.models.some((m) => m.available))
  const first = available.find((p) => p.id === preferredProvider) ?? available[0]
  const selection = picked ?? (first ? { provider: first.id, model: first.models.find((m) => m.available)!.id } : null)
  const provider = choices.find((p) => p.id === selection?.provider)
  const selectedModel = provider?.models.find((m) => m.id === selection?.model)
  return { query, choices, selection, provider, selectedModel, setPicked, ready: !query.isError && !!provider?.available && !!selectedModel?.available }
}

export function AnnotationModelSelector({ model, disabled = false }: { model: ReturnType<typeof useAnnotationModel>; disabled?: boolean }) {
  const id = useId()
  const { query, choices, selection, provider, selectedModel, setPicked } = model
  return <fieldset disabled={disabled} className="space-y-2">
    <div className="grid gap-2 sm:grid-cols-2">
      <div><label htmlFor={`${id}-provider`} className="mb-1 block text-sm">AI provider</label>
        <select id={`${id}-provider`} className={selectClass} value={selection?.provider ?? ''} disabled={query.isPending} onChange={(event) => {
          const next = choices.find((p) => p.id === event.target.value)
          if (next) setPicked({ provider: next.id, model: next.models.find((m) => m.available)?.id ?? '' })
        }}>
          {!selection && <option value="">{query.isPending ? 'Loading models…' : 'No provider available'}</option>}
          {choices.map((p) => <option key={p.id} value={p.id} disabled={!p.available}>{p.label}{p.available ? '' : ' (unavailable)'}</option>)}
        </select>
      </div>
      <div><label htmlFor={`${id}-model`} className="mb-1 block text-sm">AI model</label>
        <select id={`${id}-model`} className={selectClass} value={selection?.model ?? ''} disabled={!provider?.available} onChange={(event) => {
          if (selection) setPicked({ ...selection, model: event.target.value })
        }}>
          {!selectedModel && <option value={selection?.model ?? ''}>{selection?.model ? `${selection.model} (unavailable)` : 'Choose an available provider'}</option>}
          {provider?.models.map((m) => <option key={m.id} value={m.id} disabled={!m.available}>{m.label}{m.available ? '' : ' (unavailable)'}</option>)}
        </select>
      </div>
    </div>
    {query.isError && <p role="alert" className="text-sm text-danger">{query.error.message}</p>}
    {provider?.note && <p className="text-xs text-ink-2">{provider.note}</p>}
    {selectedModel?.note && <p className="text-xs text-ink-2">{selectedModel.note}</p>}
    {selection?.provider === 'gemini' && <p className="text-xs text-ink-2">Gemini annotation requires an image-capable endpoint and model. A reachable text-only proxy may appear available but cannot annotate images.</p>}
    {!query.isPending && !selection && <p className="text-xs text-ink-2">Configure Claude or Gemini in Settings, or start a local vision model.</p>}
    <Button variant="link" size="sm" onClick={() => {
      if (selection) setPicked(selection)
      void query.refetch()
    }} disabled={query.isFetching}>Refresh models</Button>
  </fieldset>
}
